import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Resolve Order Fields Step (workflow-native approval flow).
 *
 * Resolves the ERP-submission fields onto each (post-split, homogeneous) draft:
 * the header **order type** (`rawData.computedData.orderType`) and the per-line
 * **warehouse**, by running the org's `sales-order-draft` field
 * resolvers/extractors. Pass-through: emits the same draft ids.
 *
 * Lives in the approval chain after Compute Order Facts (and after the intake
 * split): the order-type resolver classifies the whole order, so it needs a
 * homogeneous order. This is the relational-draft port of the staged-PO
 * `resolveAndStoreFields` that v2's Split Staged PO ran after splitting; in v3
 * it's a dedicated step so it covers every path to submit (incl. no-split and
 * headless auto-approval) and re-runs idempotently on the approval loop.
 *
 * Backend-only (DB + field-resolution access); handler in
 * backend/src/workflows/steps/order-management/.
 */
export const resolveOrderFieldsStep = defineStepConfig({
  type: "resolve_order_fields",
  name: "Resolve Order Fields",
  description:
    "Resolves order type (header) + warehouse (per line) onto each homogeneous draft sales order via the org's field resolvers, for ERP submission",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    draftSalesOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of the draft sales orders to resolve fields for"),
  }),

  outputSchema: z.object({
    draftSalesOrderIds: z
      .array(z.string().uuid())
      .describe(
        "Pass-through draft sales order ids (order type + warehouse are stamped on the draft + rows)",
      ),
    resolvedOrderCount: z
      .number()
      .int()
      .describe("Number of orders that resolved a non-null order type"),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
